import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import apiService from "../../../services/api";
import { useStates } from "../../../hooks/useStates";
import useHubManagers from "../../../hooks/useHubManagers";
import useCodeCommon from "../../../hooks/useCodeCommon";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  CircularProgress,
  Alert,
  FormHelperText,
} from "@mui/material";
import { FileUploadInput, TimeRangeInput } from "../../../components/forms";
import { FILE_UPLOAD_CONFIG } from "../../../constants";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import CustomLoading from "../../../components/common/CustomLoading";

// --- Reusable Components ---
const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

const FormSection = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <Paper className="mb-4 shadow-sm border-0">
    <Box className="px-3 pt-4 pb-5">
      {title && (
        <Typography
          variant="h6"
          className="fw-bold mb-3"
          sx={{ color: "rgba(13,44,90,1)" }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Box>
  </Paper>
);

const TextInput = ({
  field,
  formData,
  onChange,
  placeholder,
  type = "text",
  label,
  required = false,
  endAdornment,
  InputLabelProps,
  error,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  placeholder?: string;
  type?: string;
  label?: string;
  required?: boolean;
  endAdornment?: React.ReactNode;
  InputLabelProps?: any;
  error?: string;
}) => (
  <TextField
    fullWidth
    size="small"
    type={type}
    variant="outlined"
    label={label}
    required={required}
    value={formData[field]}
    placeholder={placeholder}
    onChange={(e) => onChange(field, e.target.value)}
    error={Boolean(error)}
    helperText={error}
    InputProps={{
      endAdornment: endAdornment,
    }}
    InputLabelProps={InputLabelProps}
    sx={{
      "& .MuiFormLabel-asterisk": {
        color: "red",
      },
    }}
  />
);

const SelectInput = ({
  field,
  formData,
  onChange,
  options,
  label,
  required = false,
  disabled = false,
  optionLabels,
  error,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  options: string[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  optionLabels?: string[];
  error?: string;
}) => {
  
  return (
    <FormControl 
      fullWidth 
      size="small" 
      required={required} 
      disabled={disabled}
      error={Boolean(error)}
      sx={{
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.87)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
            borderWidth: 2,
          },
        },
      }}
    >
      <InputLabel
        sx={{
          // ensure the asterisk itself is red
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
          "&.Mui-focused": {
            color: "primary.main",
          },
        }}
      >
        {label || "(Select)"}
      </InputLabel>
      <Select
        value={formData[field] || ""}
        onChange={(e) => onChange(field, e.target.value)}
        label={label}
        displayEmpty
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              overflow: 'auto',
            },
          },
        }}
      >
        {options.map((opt, index) => (
          <MenuItem 
            key={opt} 
            value={opt}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                },
              },
            }}
          >
            {optionLabels ? optionLabels[index] : opt}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

const MobileInput = ({
  field,
  formData,
  onChange,
  placeholder,
  label,
  required = false,
  disabled = false,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}) => (
  <Box>
    <Typography variant="body2" className="form-label fw-medium">
      {label}
    </Typography>
    <Box
        sx={{
          display: "flex",
          border: "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: "4px",
          overflow: "hidden",
          "&:focus-within": {
            borderColor: "primary.main",
            borderWidth: "2px",
          },
        }}
    >
      <FormControl
        size="small"
        disabled={disabled}
        sx={{
          minWidth: "30%",
          flex: "0 0 30%",
          "& .MuiOutlinedInput-root": {
            backgroundColor: disabled ? "#f5f5f5" : "#f5f5f5",
            border: "none",
            borderRadius: 0,
            "& fieldset": {
              border: "none",
            },
          },
          "& .MuiInputLabel-root": {
            display: "none",
          },
          "& .MuiSelect-select": {
            padding: "8px 12px",
            color: disabled ? "#999" : "#666",
            fontWeight: 500,
          },
        }}
      >
        <Select
          value={formData[`${field}Code`] || "+91"}
          onChange={(e) => onChange(`${field}Code`, e.target.value)}
          displayEmpty
          disabled={disabled}
        >
          <MenuItem value="+91">+91</MenuItem>
          <MenuItem value="+1">+1</MenuItem>
          <MenuItem value="+44">+44</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        type="tel"
        value={formData[field]}
        placeholder={placeholder}
        onChange={(e) => onChange(field, e.target.value)}
        disabled={disabled}
        InputProps={{
          endAdornment: required ? (
            <InputAdornment position="end">
              <span style={{ color: "red" }}>*</span>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            border: "none",
            borderRadius: 0,
            "& fieldset": {
              border: "none",
            },
          },
          "& .MuiInputLabel-root": {
            display: "none",
          },
          "& .MuiInputBase-input": {
            padding: "8px 12px",
            color: disabled ? "#999" : "inherit",
          },
        }}
      />
    </Box>
  </Box>
);

// --- Main Component ---

const EditHubPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { states, loading: statesLoading, error: statesError } = useStates();
  const { hubManagers, loading: hubManagersLoading, error: hubManagersError } = useHubManagers();
  const { options: hubStatusOptions, loading: hubStatusLoading, error: hubStatusError } = useCodeCommon("HubStatus");
  const [cities, setCities] = useState<Array<{city_id: number, city_name: string}>>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  
  // Get user role for field restrictions
  const { user } = useSelector((state: RootState) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  
  const [formData, setFormData] = useState({
    hubname: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    phonenumber: "",
    phoneCode: "+91",
    hubmanagerid: "",
    status: "",
    operationhours: "",
    geofencestartradius: "",
    latitude: "",
    longitude: "",
    hubimage: "",
    id: "",
  });

  useEffect(() => {
    if (error === 'Please fill all required fields') {
      const requiredFields: (keyof typeof formData)[] = [
        'hubimage', 'hubname', 'status', 'geofencestartradius', 'address1',
        'state', 'zipcode', 'city', 'latitude', 'longitude', 'phonenumber',
        'operationhours'
      ];
      const allFilled = requiredFields.every(f => {
        const fieldValue = formData[f];
        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '';
      });

      if (allFilled) {
        setError(null);
      }
    }
  }, [formData, error]);
  

  // Build preview src from value that can be a URL, path, or JSON array string
  const resolvePreviewSrc = (value: string): string => {
    try {
      const v = String(value || "").trim();
      if (!v) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
      if (v.startsWith("[")) {
        const arr = JSON.parse(v);
        if (Array.isArray(arr) && arr.length > 0) {
          const first = String(arr[0] || "");
          if (!first) return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
          if (first.startsWith('http')) return first;
          const cleaned = first.replace(/^\/+/, '');
          return `${BASE_IMAGE_URL}${cleaned}`;
        }
      }
      if (v.startsWith('http')) return v;
      const cleaned = v.replace(/^\/+/, '');
      return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
    } catch {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
  };

  // Resolve hub id from multiple possible sources to avoid sending 0
  const location = useLocation();
  const resolveHubId = (): string | null => {
    // 1) route param
    if (id && !isNaN(Number(id)) && Number(id) > 0) return id;
    // 2) query string ?id=
    const search = new URLSearchParams(location.search);
    const qId = search.get('id');
    if (qId && !isNaN(Number(qId)) && Number(qId) > 0) return qId;
    // 3) navigation state { hubId }
    const stateAny: any = location.state;
    const sId = stateAny?.hubId ?? stateAny?.id;
    if (sId && !isNaN(Number(sId)) && Number(sId) > 0) return String(sId);
    return null;
  };

  // Fetch hub data on component mount and when hub status options are loaded
  useEffect(() => {
    const resolvedId = resolveHubId();
    if (!resolvedId) {
      setInitialLoading(false);
      setError("Invalid hub id. Please navigate from Hub Management and try again.");
      return;
    }
    // Only fetch hub data if we have status options loaded
    if (hubStatusOptions.length > 0) {
      fetchHubData(resolvedId);
    }
  }, [id, hubStatusOptions]);

  useEffect(() => {
    if (!hubManagersLoading && hubManagers?.length && formData.id) {
      const hubmasterid = String(formData.id);
  
      if (!formData.phonenumber?.trim()) {
        const managerPhone =
          hubManagers.find((m: any) => String(m.hubid) === hubmasterid)
            ?.phoneNumber;
  
        if (managerPhone) {
          setFormData(prev => ({ ...prev, phonenumber: managerPhone }));
        }
      }
    }
  }, [hubManagersLoading, hubManagers, formData.id, formData.phonenumber]);
  

  const fetchHubData = async (hubId: string) => {
    setInitialLoading(true);
    setError(null);
    try {
      const response = await apiService.getHubById(hubId);
      const hubData = (response as any)?.data || response;
      
      // Helper function to convert status codevalue to codedisplayname
      const getStatusDisplayName = (statusValue: any) => {
        const statusNumber = parseInt(statusValue?.toString() || "0");
        const statusOption = hubStatusOptions.find(status => parseInt((status.codevalue ?? '').toString()) === statusNumber);
        return statusOption ? statusOption.codedisplayname : "";
      };
      
      const hubmasterid = hubData.hubmasterid;

      let defaultPhoneNumber =
        hubData.phonenumber ||
        hubManagers?.find(
          (m: any) => String(m.hubid) === String(hubmasterid)
        )?.phoneNumber ||
        "";
      
      // Map API response to form data - handle different possible field names
      const mappedData = {
        hubname: hubData.hubname || "",
        address1: hubData.address1 || "",
        address2: hubData.address2 || "",
        city: hubData.city?.toString() || "",
        state: hubData.state?.toString() || "",
        zipcode: hubData.zipcode || "",
        phonenumber: defaultPhoneNumber || "",
        phoneCode: "+91",
        hubmanagerid: hubData.hubmanagerid?.toString() || "",
        status: getStatusDisplayName(hubData.status),
        operationhours: hubData.operationhours || "",
        geofencestartradius: hubData.geofencestartradius?.toString() || "",
        latitude: hubData.latitude?.toString() || "",
        longitude: hubData.longitude?.toString() || "",
        hubimage: hubData.hubimage || "",
        id: hubData.hubmasterid?.toString() || hubId,
      };
      
      setFormData(mappedData);
      
      // Additional debugging to check if form data is set
      setTimeout(() => {
        console.log('Form data after setState:', formData);
      }, 100);

      // If state is already selected, fetch cities for that state
      if (hubData.state) {
        const stateId = parseInt(hubData.state.toString());
        if (!isNaN(stateId)) {
          fetchCitiesByState(stateId);
        }
      }
    } catch (err: any) {
      console.error('Error fetching hub data:', err);
      setError("Failed to load hub data. Please try again.");
    } finally {
      setInitialLoading(false);
    }
  };

  // Fetch cities by state
  const fetchCitiesByState = async (stateId: number) => {
    setCitiesLoading(true);
    try {
      const response = await apiService.getCitiesByState(stateId);
      
      // Handle different possible response structures
      let citiesData: any[] = [];
      if (Array.isArray(response)) {
        citiesData = response;
      } else if (response && (response as any).data) {
        citiesData = (response as any).data;
      } else if (response && (response as any).result) {
        citiesData = (response as any).result;
      } else if (response && (response as any).cities) {
        citiesData = (response as any).cities;
      }
      
      // Filter out invalid cities and ensure they have required properties
      const validCities = (citiesData || []).filter((city: any) => {
        return city && 
          typeof city === 'object' && 
          city.city_id !== undefined && 
          city.city_name !== undefined;
      });
      
      setCities(validCities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setError('Failed to load cities. Please try again.');
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // clear error for this field on valid input
    setErrors((prev) => {
      const next = { ...prev };
      if (String(value ?? '').trim() !== '') delete (next as any)[field];
      if (field === 'state') delete (next as any)['city'];
      return next;
    });
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Autofill phone number when hub manager changes
      if (field === 'hubmanagerid') {
        const manager = (hubManagers || []).find((m: any) => String(m.userid) === String(value));
        const phone = manager?.phoneNumber || '';
        updated.phonenumber = phone;
      }
      return updated;
    });
    
    // If state is changed, fetch cities for that state
    if (field === 'state' && value) {
      const stateId = parseInt(value);
      if (!isNaN(stateId)) {
        fetchCitiesByState(stateId);
      } else {
        setCities([]);
      }
    }
  };

 

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      const nextErrors: Record<string, string> = {};
      const requireField = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        }
      };

      requireField('hubimage', 'Hub Image');
      requireField('hubname', 'Hub Name');
      requireField('status', 'Status');
      requireField('geofencestartradius', 'Geofence Radius');
      requireField('address1', 'Address 1');
      requireField('state', 'State');
      requireField('zipcode', 'Pin Code');
      requireField('city', 'City');
      requireField('latitude', 'Latitude');
      requireField('longitude', 'Longitude');
      requireField('phonenumber', 'Phone Number');
      requireField('operationhours', 'Operating Hours');

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        throw new Error('Please fill all required fields');
      }

      // Prepare data for API
      const apiData = {
        hubmasterid: parseInt(formData.id) || 0,
        hubname: formData.hubname,
        latitude: parseFloat(formData.latitude) || 0,
        longitude: parseFloat(formData.longitude) || 0,
        address1: formData.address1,
        address2: formData.address2,
        city: parseInt(formData.city) || 0,
        state: parseInt(formData.state) || 0,
        country: 0,
        zipcode: formData.zipcode,
        status: (() => {
          const selectedStatus = hubStatusOptions.find(status => status.codedisplayname === formData.status);
          const codeValue = selectedStatus?.codevalue;
          const num = parseInt((codeValue ?? '').toString());
          return isNaN(num) ? 0 : num;
        })(),
        hubmanagerid: parseInt(formData.hubmanagerid) || 0,
        userid: 0,
        operationhours: formData.operationhours,
        geofencesendradius: parseFloat(formData.geofencestartradius) || 0,
        geofencestartradius: parseFloat(formData.geofencestartradius) || 0,
        phonenumber: formData.phonenumber,
        whatsappnumber: "",
        hubimage: formData.hubimage,
      };

      await apiService.updateHub(formData.id, apiData);
      
      // Navigate back to hub management page
      navigate('/hub-management', { state: location.state });
    } catch (err: any) {
      console.error('Error updating hub:', err);
      setError(err.message || "Failed to update hub");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <CustomLoading message="Fetching Hub Details..." />;
  }

  return (
    <Container fluid>
      {/* Error Display */}
      {(error || statesError || hubManagersError || hubStatusError) && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || statesError || hubManagersError || hubStatusError}
        </Alert>
      )}

      <FormSection>
        <Row className="g-3">
          {/* Row 1 */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <FileUploadInput
              field="hubimage"
              formData={formData}
              onChange={handleInputChange}
              required
              error={errors.hubimage}
              folderPath={FILE_UPLOAD_CONFIG.FOLDER_PATHS.HUBS}
              folderType={FILE_UPLOAD_CONFIG.FOLDER_TYPES.DOCUMENT}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="hubname"
              formData={formData}
              onChange={handleInputChange}
              label="Hub Name"
              required
              error={errors.hubname}
              placeholder="Enter hub name"
            />
            {/* Debug info */}
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="hubmanagerid"
              formData={formData}
              onChange={handleInputChange}
              label="Hub Manager"
              disabled={hubManagersLoading || !isSuperAdmin}
              options={hubManagersLoading ? ["Loading..."] : (hubManagers && hubManagers.length > 0 ? hubManagers.map(manager => manager.userid.toString()) : ["No managers available"])}
              optionLabels={hubManagersLoading ? ["Loading..."] : (hubManagers && hubManagers.length > 0 ? hubManagers.map(manager => manager.name) : ["No managers available"])}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <MobileInput
              key={formData.phonenumber}
              field="phonenumber"
              formData={formData}
              onChange={handleInputChange}
              required
              disabled={!isSuperAdmin}
              placeholder="Enter phone number"
            />
          </Col>
          
          {/* Row 2 */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TimeRangeInput
              field="operationhours"
              formData={formData}
              onChange={handleInputChange}
              label="Operating Hours"
              required
              error={errors.operationhours}
              placeholder="e.g., 9:00 AM - 6:00 PM"
              endAdornment={
                <InputAdornment position="end">
                  <span style={{ color: "#666" }}>🕐</span>
                </InputAdornment>
              }
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="status"
              formData={formData}
              onChange={handleInputChange}
              label="Status"
              required
              disabled={hubStatusLoading}
              options={hubStatusLoading ? ["Loading..."] : (hubStatusOptions && hubStatusOptions.length > 0 ? hubStatusOptions.map(status => status.codedisplayname) : ["No status available"])}
              optionLabels={hubStatusLoading ? ["Loading..."] : (hubStatusOptions && hubStatusOptions.length > 0 ? hubStatusOptions.map(status => status.codedisplayname) : ["No status available"])}
              error={errors.status}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="geofencestartradius"
              formData={formData}
              onChange={handleInputChange}
              label="Geofence Radius"
              required
              error={errors.geofencestartradius}
              placeholder="Enter radius in meters"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="address1"
              formData={formData}
              onChange={handleInputChange}
              label="Address 1"
              required
              error={errors.address1}
              placeholder="Enter address line 1"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="address2"
              formData={formData}
              onChange={handleInputChange}
              label="Address 2"
              placeholder="Enter address line 2"
            />
          </Col>
          
          {/* Row 3 */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <SelectInput
              field="state"
              formData={formData}
              onChange={handleInputChange}
              label="State"
              required
              disabled={statesLoading}
              options={statesLoading ? ["Loading..."] : (states && states.length > 0 ? states.map(state => state?.state_id?.toString() || "") : ["No states available"])}
              optionLabels={statesLoading ? ["Loading..."] : (states && states.length > 0 ? states.map(state => state?.state_name || "") : ["No states available"])}
              error={errors.state}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="zipcode"
              formData={formData}
              onChange={handleInputChange}
              label="Pin Code"
              required
              error={errors.zipcode}
              placeholder="Enter pin code"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="city"
              formData={formData}
              onChange={handleInputChange}
              label="City"
              required
              disabled={citiesLoading || !formData.state}
              options={citiesLoading ? ["Loading..."] : (cities && cities.length > 0 ? cities.map(city => city?.city_id?.toString() || "") : ["Select state first"])}
              optionLabels={citiesLoading ? ["Loading..."] : (cities && cities.length > 0 ? cities.map(city => city?.city_name || "") : ["Select state first"])}
              error={errors.city}
            />
          </Col>
          
          {/* Row 4 */}
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              field="latitude"
              formData={formData}
              onChange={handleInputChange}
              label="Latitude"
              required
              error={errors.latitude}
              placeholder="Enter latitude"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="longitude"
              formData={formData}
              onChange={handleInputChange}
              label="Longitude"
              required
              error={errors.longitude}
              placeholder="Enter longitude"
            />
          </Col>
        </Row>
      </FormSection>

      {/* Attachments Section */}
      <FormSection title="Attachments">
        <Row className="g-3">
          {formData.hubimage && (
            <Col xs={12} sm={6} md={4}>
              <ImagePreviewCard
                title="Hub Image"
                value={formData.hubimage}
                resolveSrc={resolvePreviewSrc}
                size={200}
                defaultImage={DefaultImage}
                onDelete={() => setFormData((prev) => ({ ...prev, hubimage: "" }))}
              />
            </Col>
          )}

          {!formData.hubimage && (
            <Col xs={12}>
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '2px dashed #dee2e6'
              }}>
                <Typography variant="body1" sx={{ color: '#6c757d' }}>
                  No attachments uploaded yet
                </Typography>
              </Box>
            </Col>
          )}
        </Row>
      </FormSection>

      {/* Action Buttons */}
      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={handleSave}
          disabled={loading}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Updating...
            </Box>
          ) : (
            'Update'
          )}
        </Button>
        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={() => navigate("/hub-management", { state: location.state })}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": {
              borderColor: "rgba(13,44,90,1)",
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default EditHubPage;
